import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

import { proposePrices, type Basis, type Market, type PriceRuns, type ProposeAnswer, type ProposeChannel, type TickedRow } from "./api";
import { invalidateBatch } from "./batch";
import { placeName, PROPOSE_COPY as T, proposeBody, proposeRefusal, proposeRowKey, replaceCount, rowBadge, sheetBusy, skippedGroups, unverifiedLines, type MarketplaceNames, type SheetState } from "./propose";

/** Rows the preview table shows before `+ N more` (mockup v28 frame 2). */
export const PREVIEW_ROWS = 25;
export const CHANNEL_WORD: Record<ProposeChannel, string> = { all: "All", amazon: "Amazon", shopify: "Shopify" };

type Scope = { market: Market; basis: Basis; channel: ProposeChannel; ticked: TickedRow[] };

/**
 * The Propose sheet (§9.10 ProposeSheet, mockup v28 frame 2). Opening it asks C2 for a dry run: every check, nothing saved. The
 * preview names the counts, the skipped rows by reason and the rules that could not be checked. Propose runs the real pass; C2
 * saves the lines in one transaction (R3) and never commits them (INV-4). While a pass runs every action is off and the sheet
 * cannot close, so nothing is sent twice. Radix traps focus inside the sheet and returns it to the button on close.
 */
export function ProposeSheet({ open, scope, runs, placeNames, areaName, onClose, onSaved }: {
  open: boolean; scope: Scope; runs: PriceRuns | undefined; placeNames: MarketplaceNames; areaName: string; onClose: () => void; onSaved: (answer: ProposeAnswer) => void;
}) {
  const client = useQueryClient();
  const [state, setState] = useState<SheetState>({ phase: "checking" });
  const [replace, setReplace] = useState(false);
  const elapsed = useElapsed(sheetBusy(state) && open);
  const sent = useRef(0);

  const pass = (dryRun: boolean, last: ProposeAnswer | null) => {
    const id = ++sent.current;
    setState(dryRun ? { phase: "checking" } : { phase: "saving", answer: last as ProposeAnswer });
    proposePrices(proposeBody(scope.market, scope.basis, scope.channel, scope.ticked, dryRun, replace)).then(
      (answer) => {
        if (id !== sent.current) return;
        if (dryRun) { setState({ phase: "preview", answer }); return; }
        setState({ phase: "done", answer });
        invalidateBatch(client);
        onSaved(answer);
      },
      (error) => { if (id === sent.current) setState({ phase: "error", text: proposeRefusal(error, areaName), answer: last }); },
    );
  };
  // Each opening is a fresh preview of the scope on screen.
  useEffect(() => { if (open) { setReplace(false); pass(true, null); } else sent.current++; }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const busy = sheetBusy(state);
  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
      <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-[720px]"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }} onInteractOutside={(e) => { if (busy) e.preventDefault(); }}>
        <SheetHeader>
          <SheetTitle>{T.title(scope.market)}</SheetTitle>
          <SheetDescription>{scope.ticked.length > 0 ? T.ticked(scope.ticked.length) : `${T.channel}: ${CHANNEL_WORD[scope.channel]}`}</SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-4">
          <ProposeSheetBody state={state} runs={runs} placeNames={placeNames} ticked={scope.ticked.length} replace={replace} elapsed={elapsed}
            onReplace={setReplace}
            onPropose={() => state.phase === "preview" && pass(false, state.answer)}
            onRetry={() => pass(state.phase === "error" && state.answer ? false : true, state.phase === "error" ? state.answer : null)}
            onCancel={onClose} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Seconds since `running` turned on; 0 while off. */
function useElapsed(running: boolean): number {
  const [s, setS] = useState(0);
  useEffect(() => {
    setS(0);
    if (!running) return;
    const started = Date.now();
    const t = window.setInterval(() => setS(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(t);
  }, [running]);
  return s;
}

/** The sheet's body for each state. Rendered on its own in tests: the sheet's portal does not render server-side. */
export function ProposeSheetBody({ state, runs, placeNames, ticked, replace, elapsed, onReplace, onPropose, onRetry, onCancel }: {
  state: SheetState; runs: PriceRuns | undefined; placeNames?: MarketplaceNames; ticked: number; replace: boolean; elapsed: number;
  onReplace: (next: boolean) => void; onPropose: () => void; onRetry: () => void; onCancel: () => void;
}) {
  const busy = sheetBusy(state);
  if (state.phase === "checking" || state.phase === "saving") {
    return (
      <div role="status" aria-live="polite" aria-busy="true" className="flex flex-col gap-2 py-6" data-propose-phase={state.phase}>
        <p className="flex items-center gap-2 text-sm text-ks-ink"><Loader2 aria-hidden className="size-4 animate-spin" />{state.phase === "checking" ? T.checking : T.saving}</p>
        <p className="text-xs text-ks-grey-1 tabular-nums">{T.elapsed(elapsed)}</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" disabled data-nonmutating>{T.cancel}</Button>
          <Button type="button" disabled aria-busy="true">{state.phase === "checking" ? T.button : T.save(state.answer.proposed_count)}</Button>
        </div>
      </div>
    );
  }
  const answer = state.answer;
  if (state.phase === "error") {
    return (
      <div className="flex flex-col gap-3 py-4" data-propose-phase="error">
        <p role="alert" className="text-sm">{state.text}</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" data-nonmutating onClick={onCancel}>{T.cancel}</Button>
          <Button type="button" onClick={onRetry}>{T.retry}</Button>
        </div>
      </div>
    );
  }
  const a = answer as ProposeAnswer;
  const groups = skippedGroups(a, runs, placeNames);
  const unverified = unverifiedLines(a);
  const proposed = a.rows.filter((r) => r.status === "proposed");
  const done = state.phase === "done";
  const saveCount = a.proposed_count + (replace ? replaceCount(a) : 0);
  const unverifiedRules = new Set(a.rows.flatMap((r) => r.unverified.map((u) => u.rule_id))).size;
  return (
    <div className="flex flex-col gap-4" data-propose-phase={state.phase}>
      {done
        ? <p role="status" className="text-sm font-semibold text-ks-pass">{T.done(a.proposed_count)}</p>
        : <p role="status" className="text-sm font-semibold text-ks-ink">{T.preview(a.proposed_count, a.skipped_count)}</p>}
      <dl className="grid grid-cols-3 gap-2 text-center">
        <Count label={T.counts.proposed} value={a.proposed_count} />
        <Count label={T.counts.skipped} value={a.skipped_count} />
        <Count label={T.counts.unverified} value={unverifiedRules} />
      </dl>
      {unverified.length > 0 && <ul className="list-disc pl-5 text-sm text-ks-ink" data-propose-unverified>{unverified.map((line) => <li key={line}>{line}</li>)}</ul>}
      {groups.length > 0 && (
        <section aria-labelledby="pp-propose-skipped" className="flex flex-col gap-1">
          <h3 id="pp-propose-skipped" className="text-sm font-semibold text-ks-ink">{T.skippedHead}</h3>
          <SkippedList groups={groups} />
        </section>
      )}
      {proposed.length > 0 && (
        <section aria-labelledby="pp-propose-rows" className="flex flex-col gap-1">
          <h3 id="pp-propose-rows" className="text-sm font-semibold text-ks-ink">{T.rowsHead}</h3>
          <table className="w-full text-sm">
            <thead><tr>{T.columns.map((c, i) => <th key={c} className={`py-1 font-medium text-ks-grey-1 ${i > 0 ? "text-right" : "text-left"}`}>{c}</th>)}</tr></thead>
            <tbody>
              {proposed.slice(0, PREVIEW_ROWS).map((r) => (
                <tr key={proposeRowKey(r)} data-propose-row={proposeRowKey(r)}>
                  <td className="py-1">{r.product} {r.variant} · {placeName(r, placeNames)}</td>
                  <td className="py-1 text-right tabular-nums text-ks-grey-1">{r.current?.value ?? "none"}</td>
                  <td className="py-1 text-right"><span className="font-semibold tabular-nums">{r.proposed?.value}</span> {rowBadge(r) && <span className="ml-1 rounded border border-ks-grey-2 px-1 text-xs" data-propose-badge>{rowBadge(r)}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {proposed.length > PREVIEW_ROWS && <p className="text-xs text-ks-grey-1">{T.more(proposed.length - PREVIEW_ROWS)}</p>}
        </section>
      )}
      {!done && saveCount === 0 && <p className="text-sm text-ks-grey-1">{T.nothingToSave}</p>}
      {!done && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={replace} disabled={busy} onCheckedChange={(c) => onReplace(c === true)} data-propose-replace />
          {T.replace(replaceCount(a))}
        </label>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" data-nonmutating onClick={onCancel}>{done ? T.close : T.cancel}</Button>
        {!done && <Button type="button" disabled={busy || saveCount === 0} onClick={onPropose} data-propose-save>{ticked > 0 ? T.saveTicked(ticked) : T.save(saveCount)}</Button>}
      </div>
    </div>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return <div className="rounded-md border border-ks-grey-2 p-2"><dd className="text-lg font-semibold tabular-nums text-ks-ink">{value}</dd><dt className="text-xs text-ks-grey-1">{label}</dt></div>;
}

/** The skipped rows as a real list, each reason as text (§9.10 accessibility), the group count as C2 counted it. */
export function SkippedList({ groups }: { groups: ReturnType<typeof skippedGroups> }) {
  return (
    <ul className="flex flex-col gap-2 text-sm" data-propose-skipped>
      {groups.map((g) => (
        <li key={g.reason} data-skip-reason={g.reason}>
          <span className="font-semibold text-ks-ink">{g.label} ({g.count})</span>
          <ul className="pl-4 text-ks-grey-1">
            {g.items.map((item) => <li key={item.key} title={item.message}>{item.name}: {item.label}</li>)}
          </ul>
        </li>
      ))}
    </ul>
  );
}
